import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import {
  BaseTransaction,
  BlacklistData,
  Response,
  TransactionWarning,
} from '@ui/src';
import { internalTransactionsListApi } from '../../../internal/store/services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';

export interface TransactionFeeState {
  panelIsOpen: boolean;
  transactionId?: string;
  transaction?: BaseTransaction;
  warnings?: TransactionWarning[];
  blacklistDataVisible: boolean;
  blacklistData?: BlacklistData;
}

const initialState: TransactionFeeState = {
  panelIsOpen: true,
  blacklistDataVisible: false,
};

export const tansactionFeeSlice = createSlice({
  name: 'tansactionFee',
  initialState,
  reducers: {
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.transactionId && action.payload.state) {
        state.transactionId = action.payload.transactionId;
      }
    },
    setWarnings: (state, action: PayloadAction<TransactionWarning[]>) => {
      state.warnings = action.payload;
    },
    setBlacklistDataVisible: (state, action: PayloadAction<boolean>) => {
      state.blacklistDataVisible = action.payload;
    },
    setBlacklistData: (
      state,
      action: PayloadAction<BlacklistData | undefined>,
    ) => {
      state.blacklistData = action.payload;
    },
    clearTransactionFeeState: (state) => {
      state.transactionId = undefined;
      state.transaction = undefined;
      state.panelIsOpen = false;
      state.warnings = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        internalTransactionsListApi.endpoints.getInternalTransactionById
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          const prevTransaction = state.transaction;
          const nextTransaction = action.payload.data;

          if (!prevTransaction) {
            state.transaction = nextTransaction;
          } else if (!isEqual(prevTransaction, nextTransaction)) {
            state.transaction = nextTransaction;
          }
        },
      )
      .addMatcher(
        internalTransactionsListApi.endpoints.getInternalTransactionWarnings
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionWarning>>) => {
          const prevWarnings = state.warnings;
          const nextWarnings = action.payload.data.edges.map(
            (item) => item.node,
          );

          if (!prevWarnings) {
            state.warnings = nextWarnings;
          } else if (!isEqual(prevWarnings, nextWarnings)) {
            state.warnings = nextWarnings;
          }
        },
      )
      .addMatcher(
        internalTransactionsListApi.endpoints
          .applyInternalTransactionWarningAction.matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionWarning }>) => {
          const nextWarnings = state.warnings?.map((item) => {
            let result = { ...item };

            if (action.payload.data.id === result.id) {
              result = action.payload.data;
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      )
      .addMatcher(
        statusesTransactionsApi.endpoints.changeTransactionStatus
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          if (state.transaction) {
            const nextTransaction = { ...state.transaction };

            if (nextTransaction.id === action.payload.data.id) {
              nextTransaction.baseellaStatus =
                action.payload.data.baseellaStatus;
            }

            state.transaction = nextTransaction;
          }
        },
      );
  },
});

export const {
  changePanelState,
  clearTransactionFeeState,
  setWarnings,
  setBlacklistData,
  setBlacklistDataVisible,
} = tansactionFeeSlice.actions;

export default tansactionFeeSlice.reducer;
